import React, { useCallback, useMemo, useState } from 'react';
import { SongTiktokAudioPostStats } from '@round/api';
import PaginationTable from '../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import {
    buildTiktokMusicUrl,
    buildTiktokPostUrl,
    buildTiktokUserUrl,
    displayOptionalNumericTableValue,
    formatDateObjShort,
    numberWithCommas,
} from '../../../../../helpers';
import styles from './TopTiktokAudioPosts.module.css';
import Skeleton from '../../../../../ui/Skeleton/Skeleton';
import SearchInput from '../../../../../ui/DataEntry/SearchInput/SearchInput';
import { Column } from 'react-table';
import AccountCell from '../../../../../SharedComponents/TableComponents/AccountCell/AccountCell';
import TruncatedTextCell from '../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import TitleCell from '../../../../../SharedComponents/TableComponents/TitleCell/TitleCell';
import CountryCell from '../../../../../SharedComponents/TableComponents/CountryCell/CountryCell';

export type TopTiktokAudioPostsTableProps = {
    rows: SongTiktokAudioPostStats[];
    count: number;
    loading: boolean;
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (page: number | undefined) => void;
    search: string;
    setSearch: (search: string) => void;
};

const TopTiktokAudioPosts = ({
    page,
    search,
    setSearch,
    setPage,
    pageSize,
    setPageSize,
    loading,
    rows,
    count,
}: TopTiktokAudioPostsTableProps) => {
    const [tiktokTopPostsRowsDisplayed, setTiktokTopPostsRowsDisplayed] = useState(0);
    const topPostsRecordsShowingLabel = `displaying ${tiktokTopPostsRowsDisplayed} post${
        tiktokTopPostsRowsDisplayed === 1 ? '' : 's'
    } out of ${numberWithCommas(count)} analysed`;

    const columns = useMemo<Column<SongTiktokAudioPostStats>[]>(
        () => [
            {
                Header: 'Account',
                accessor: 'author_username',
                disableSortBy: true,
                Cell: (data) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <AccountCell
                            accountName={data.row.original.author_name}
                            accountUsername={data.value}
                            accountUrl={buildTiktokUserUrl(data.value)}
                        />
                    );
                },
            },
            {
                Header: 'Followers',
                accessor: 'author_follower_count',
                disableSortBy: true,
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Location',
                accessor: 'location',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <CountryCell countryCode={value} />;
                },
            },
            {
                Header: 'Video Caption',
                accessor: 'caption',
                disableSortBy: true,
                Cell: ({ value }) => <>{loading ? <Skeleton /> : <TruncatedTextCell>{value}</TruncatedTextCell>}</>,
            },
            {
                Header: 'Song',
                accessor: 'audio_title',
                disableSortBy: true,
                Cell: (data) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a
                            onClick={(e) => e.stopPropagation()}
                            className={styles.link}
                            href={buildTiktokMusicUrl(data.row.original.audio_title, data.row.original.audio_tiktok_id)}
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            <TitleCell title={data.value} subtitle={data.row.original.audio_author_name} />
                        </a>
                    );
                },
            },
            {
                Header: 'Audio',
                accessor: 'audio_is_original',
                disableSortBy: true,
                Cell: ({ value: isOriginal }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    if (!isOriginal) {
                        return <>Official Audio</>;
                    }

                    return <>{'Original Audio'}</>;
                },
            },
            {
                Header: 'Date Posted',
                accessor: 'create_time',
                Cell: (data) => (
                    <>{loading ? <Skeleton /> : formatDateObjShort(new Date(data.row.original.create_time * 1000))}</>
                ),
            },
            {
                Header: 'Plays',
                accessor: 'play_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Shares',
                accessor: 'share_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
            {
                Header: 'Saves',
                accessor: 'save_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : displayOptionalNumericTableValue(value)}</>,
            },
        ],
        [loading]
    );

    const navigateToTikTokPost = useCallback((item: SongTiktokAudioPostStats) => {
        window.open(buildTiktokPostUrl(item.author_username, item.post_tiktok_id), '_blank', 'noopener noreferrer');
    }, []);

    return (
        <>
            <div className={styles.titles}>
                <p className={styles.title}>Top Posts</p>
                <p className={styles.subtitle}>{loading ? <Skeleton width="20rem" /> : topPostsRecordsShowingLabel}</p>
            </div>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <SearchInput value={search} onChange={setSearch} />
                </div>
            </div>
            <PaginationTable
                tableClassName={styles.table}
                theadClassName={styles.thead}
                tbodyClassName={styles.tbody}
                loading={loading}
                data={rows}
                columns={columns}
                count={count}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={(value) => setPageSize(value ?? 10)}
                onRowClick={navigateToTikTokPost}
                onPaginationChange={(state) => setTiktokTopPostsRowsDisplayed(state.pageRows.length)}
            />
        </>
    );
};

export default TopTiktokAudioPosts;
